import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const PWP_HANDLES = new Set([
  'ruvine-tumbler-full-set',
  'ruvine-hair-scalp',
  'ruvine-dumpling-pouch',
  'pretty-club-tote',
  'nukd-2-in-1-full-size-refill-pack',
  'pwp-nukd-single-pack',
  'pwp-nukd-refill-pack',
  'ruvine-key-chain-brown',
  'ruvine-key-chain-pink',
  'ruvine-pink-mini-pouch',
  'ruvine-hair-scrunchie',
  'ruvine-beauty-pouch',
  'ruvine-pink-hair-band',
  'ruvine-elegant-hairclip',
  'ruvine-hair-applicator',
  'ruvine-eye-mask-pink',
  'ruvine-pretty-club-out-of-routine-set',
  'ruvine-mini-umbrella',
  'ruvine-sun-hat',
  'pretty-club-waterproof-makeup-bag-s',
  'pretty-club-waterproof-makeup-bag-m',
]);

const PWP_ADD_HANDLER_KEY = '__ruvinePwpAddHandlerBound';
let pwpAddInFlight = false;

function isTrigger(item) {
  const title = String(item.product_title || '').toLowerCase();
  const type = String(item.product_type || '').toLowerCase();
  const handle = String(item.handle || '').toLowerCase();

  if (item.properties?._ruvine_pwp === 'true' || item.properties?._ruvine_gift === 'true') return false;
  if (PWP_HANDLES.has(handle) || type === 'pwp add-on') return false;
  if (type === 'hair essence' || type === 'lash essence') return true;
  if (title.includes('hair essence') || title.includes('lash essence')) return true;
  if (title.includes('nukd dewy cushion')) return true;
  if (handle === 'sunday-kit' || title.includes('sunday kit')) return true;
  return [
    'duo set bundle',
    'trio set bundle',
    'ultimate set bundle',
    'lash & hair duo',
    'ruvine pretty set',
    'pretty complete set',
  ].some((name) => title.includes(name));
}

function getPwpQuantityForVariant(cart, variantId) {
  return cart.items.reduce((total, item) => {
    const isPwp = String(item.properties?._ruvine_pwp || '').toLowerCase() === 'true';
    const isSameVariant = Number(item.variant_id) === Number(variantId);
    return total + (isPwp && isSameVariant ? Number(item.quantity || 0) : 0);
  }, 0);
}

function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean)
    .filter((sectionId, index, sectionIds) => sectionIds.indexOf(sectionId) === index);
}

function setPwpButtonsLoading(isLoading) {
  document.querySelectorAll('[data-ruvine-pwp-add]').forEach((pwpButton) => {
    if (!(pwpButton instanceof HTMLButtonElement)) return;

    if (isLoading) {
      pwpButton.setAttribute('aria-busy', 'true');
    } else {
      pwpButton.removeAttribute('aria-busy');
    }
  });
}

async function getCart() {
  const response = await fetch(Theme.routes.cart_url + '.js', {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error('Unable to refresh the cart.');
  return response.json();
}

async function handlePwpAdd(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-ruvine-pwp-add]') : null;
  if (
    !(button instanceof HTMLButtonElement) ||
    button.disabled ||
    button.dataset.loading === 'true' ||
    pwpAddInFlight
  ) {
    return;
  }

  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  const originalText = button.textContent;
  event.preventDefault();
  event.stopPropagation();
  pwpAddInFlight = true;
  button.dataset.loading = 'true';
  button.disabled = true;
  button.textContent = 'Adding…';
  setPwpButtonsLoading(true);

  try {
    const cartBefore = await getCart();
    const qualified = cartBefore.items.some(isTrigger);
    const pwpQuantity = getPwpQuantityForVariant(cartBefore, variantId);

    if (!qualified) throw new Error('Add a qualifying Ruvine product first.');
    if (pwpQuantity >= 2) throw new Error('Maximum 2 of each PWP add-on per order.');

    const sectionIds = getCartSectionIds();

    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _ruvine_pwp: 'true' } }],
          sections: sectionIds.join(','),
          // Keep the section render tied to the current page. Without this,
          // Shopify can return stale cart-drawer markup after a navigation.
          sections_url: window.location.pathname + window.location.search,
        }),
      })
    );

    const responseData = await response.json();

    if (!response.ok || responseData.status) {
      throw new Error(responseData.description || responseData.message || 'Unable to add this item.');
    }

    const cartAfter = await getCart();
    document.dispatchEvent(
      new CartAddEvent(cartAfter, button.id || 'ruvine-cart-pwp', {
        source: 'ruvine-cart-pwp',
        itemCount: 1,
        sections: responseData.sections,
      })
    );
  } catch (error) {
    console.error(error);
    button.disabled = false;
    button.textContent = error instanceof Error && error.message.includes('Maximum') ? 'Max 2' : 'Try again';
    window.setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 1800);
  } finally {
    pwpAddInFlight = false;
    setPwpButtonsLoading(false);
    delete button.dataset.loading;
  }
}

// The cart drawer is re-rendered after every cart update. Bind once at the
// document level so the newly rendered PWP buttons always use the same handler.
if (!window[PWP_ADD_HANDLER_KEY]) {
  window[PWP_ADD_HANDLER_KEY] = true;
  // Capture phase avoids losing a click when another drawer component stops
  // bubbling while its cart markup is being refreshed.
  document.addEventListener('click', handlePwpAdd, true);
}
